import React from 'react';
import { 
  Server, 
  Database, 
  Zap, 
  MessageSquare, 
  Network, 
  Code, 
  HardDrive, 
  Globe,
  Route,       
  Box,         
  Shield,      
  LayoutGrid,  
  Search,      
  Cpu,         
  Lock,        
  Mail,
  SquareDashed,
  Activity,
  Workflow,
  CornerUpRight,
  Minus,
  ArrowRightLeft,
  Repeat
} from 'lucide-react';
import { ComponentType, ConnectionType } from './types';

// Use explicit array with string literals to avoid enum initialization issues
export const CONNECTION_TYPES: ConnectionType[] = [
  'HTTP' as ConnectionType,
  'HTTPS' as ConnectionType,
  'TCP' as ConnectionType,
  'UDP' as ConnectionType,
  'gRPC' as ConnectionType,
  'JDBC' as ConnectionType,
  'AMQP' as ConnectionType,
  'WebSocket' as ConnectionType,
  'Internal' as ConnectionType
];

// Create icon factory to avoid initialization issues with JSX
const createIcon = (IconComponent: React.ComponentType<{ className?: string }>) => 
  React.createElement(IconComponent, { className: "w-5 h-5" });

// Connection type configuration with icons and colors
export const CONNECTION_TYPE_CONFIG: Record<ConnectionType, {
  icon: React.ReactNode;
  color: string;
  strokeColor: string;
  description: string;
}> = {
  [ConnectionType.HTTP]: {
    icon: createIcon(Globe),
    color: '#3b82f6', // blue
    strokeColor: '#2563eb',
    description: 'HTTP connection'
  },
  [ConnectionType.HTTPS]: {
    icon: createIcon(Shield),
    color: '#10b981', // green
    strokeColor: '#059669',
    description: 'HTTPS secure connection'
  },
  [ConnectionType.TCP]: {
    icon: createIcon(Network),
    color: '#6366f1', // indigo
    strokeColor: '#4f46e5',
    description: 'TCP connection'
  },
  [ConnectionType.UDP]: {
    icon: createIcon(Network),
    color: '#8b5cf6', // purple
    strokeColor: '#7c3aed',
    description: 'UDP connection'
  },
  [ConnectionType.GRPC]: {
    icon: createIcon(Code),
    color: '#ec4899', // pink
    strokeColor: '#db2777',
    description: 'gRPC connection'
  },
  [ConnectionType.JDBC]: {
    icon: createIcon(Database),
    color: '#f59e0b', // amber
    strokeColor: '#d97706',
    description: 'JDBC database connection'
  },
  [ConnectionType.AMQP]: {
    icon: createIcon(MessageSquare),
    color: '#14b8a6', // teal
    strokeColor: '#0d9488',
    description: 'AMQP message queue'
  },
  [ConnectionType.WEBSOCKET]: {
    icon: createIcon(Globe),
    color: '#06b6d4', // cyan
    strokeColor: '#0891b2',
    description: 'WebSocket connection'
  },
  [ConnectionType.INTERNAL]: {
    icon: createIcon(Box),
    color: '#6b7280', // gray
    strokeColor: '#4b5563',
    description: 'Internal connection'
  }
};

// Path style configuration with icons
export const PATH_STYLE_CONFIG = {
  'default': {
    icon: Activity,
    label: 'Bezier (Curved)',
    description: 'Smooth curved path'
  },
  'smoothstep': {
    icon: Workflow,
    label: 'Smooth Step (Angled)',
    description: 'Angled path with smooth corners'
  },
  'step': {
    icon: CornerUpRight,
    label: 'Step (Orthogonal)',
    description: 'Right-angled path'
  },
  'straight': {
    icon: Minus,
    label: 'Straight',
    description: 'Direct straight line'
  }
};

// Minimal Monochrome Configuration
const MONO_STYLE = {
  color: 'bg-white text-black',
  borderColor: 'border-black',
};

// Direct initialization - use string keys to avoid enum initialization issues
const nodeConfigMap: Record<string, { 
  icon: React.ReactNode, 
  color: string, 
  borderColor: string,
  description: string 
}> = {
  'ApiGateway': {
    icon: createIcon(Route),
    description: 'Entry point for clients',
    ...MONO_STYLE
  },
  'WebServer': {
    icon: createIcon(Server),
    description: 'Handles HTTP requests',
    ...MONO_STYLE
  },
  'Microservice': {
    icon: createIcon(Box),
    description: 'Generic service container',
    ...MONO_STYLE
  },
  'LoadBalancer': {
    icon: createIcon(Network),
    description: 'Distributes traffic',
    ...MONO_STYLE
  },
  'ServerlessFunction': {
    icon: createIcon(Code),
    description: 'Serverless compute',
    ...MONO_STYLE
  },
  'Worker': {
    icon: createIcon(Cpu),
    description: 'Background processor',
    ...MONO_STYLE
  },
  'RDBMS': {
    icon: createIcon(Database),
    description: 'Relational Database',
    ...MONO_STYLE
  },
  'Cache': {
    icon: createIcon(Zap),
    description: 'In-memory data store',
    ...MONO_STYLE
  },
  'SearchEngine': {
    icon: createIcon(Search),
    description: 'Dedicated search service',
    ...MONO_STYLE
  },
  'MessageBroker': {
    icon: createIcon(MessageSquare),
    description: 'Event streaming/Queue',
    ...MONO_STYLE
  },
  'ObjectStorage': {
    icon: createIcon(HardDrive),
    description: 'Blob/Object storage',
    ...MONO_STYLE
  },
  'CDN': {
    icon: createIcon(Globe),
    description: 'Content Delivery Network',
    ...MONO_STYLE
  },
  'Firewall': {
    icon: createIcon(Shield),
    description: 'Network security',
    ...MONO_STYLE
  },
  'Kubernetes': {
    icon: createIcon(LayoutGrid),
    description: 'Orchestration cluster',
    ...MONO_STYLE
  },
  'AuthService': {
    icon: createIcon(Lock),
    description: 'Identity & Access',
    ...MONO_STYLE
  },
  'NotificationService': {
    icon: createIcon(Mail),
    description: 'Email/SMS Service',
    ...MONO_STYLE
  },
  'Group': {
    icon: createIcon(SquareDashed),
    description: 'Component Group',
    color: 'bg-transparent text-black',
    borderColor: 'border-dashed border-gray-400'
  }
};

// Export with proper typing using ComponentType enum values as keys
export const NODE_CONFIG: Record<ComponentType, { 
  icon: React.ReactNode, 
  color: string, 
  borderColor: string,
  description: string 
}> = nodeConfigMap as Record<ComponentType, { 
  icon: React.ReactNode, 
  color: string, 
  borderColor: string,
  description: string 
}>;
